/**
 * @fileoverview Event Edit Form component.
 * @summary Form for editing event details (name, description, date, time, venue, status).
 *
 * @package KaraoNow
 * @module apps/front/src/pages/organizer/components/EventEditForm
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/free-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import Spinner from '../../../components/Spinner'
import type { Event } from '../../../store/slices/eventsSlice'
import { EventFormFields } from './EventFormFields'

interface Venue {
  id: string
  name: string
  city?: string
}

interface EventEditFormProps {
  isOpen: boolean
  formData: {
    name: string
    description: string
    venueId: string
    date: string
    time: string
    status: Event['status']
  }
  isSaving: boolean
  formError: string | null
  venues: Venue[]
  onFormDataChange: (data: {
    name?: string
    description?: string
    venueId?: string
    date?: string
    time?: string
    status?: Event['status']
  }) => void
  onSubmit: (e: React.FormEvent) => void
  onCancel: () => void
  eventId?: string // Optional: required for quota validation on date change
  onDateQuotaError?: (error: { message: string; currentCount?: number; limit?: number }) => void // Callback when quota is exceeded
  originalDate?: string // Original event date (to detect if date changed)
  planType?: 'free' | 'one_shot' | 'subscription' // Event plan type (for ONE_SHOT date limits)
  oneShotExpiresAt?: string | Date // ONE_SHOT expiration date (for max date limit)
}

/**
 * @summary Event edit form component.
 * @param {EventEditFormProps} props - Component props.
 * @returns {React.ReactElement} Event edit form or null if not open.
 */
export function EventEditForm({
  isOpen,
  formData,
  isSaving,
  formError,
  venues,
  onFormDataChange,
  onSubmit,
  onCancel,
  eventId,
  onDateQuotaError,
  originalDate,
  planType,
  oneShotExpiresAt,
}: EventEditFormProps) {
  const { t } = useTranslation()

  if (!isOpen) {
    return null
  }

  return (
    <div className="card mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-(--color-text-primary)">
          {t('events.editEvent')}
        </h2>
        <button
          onClick={onCancel}
          className="text-(--color-text-secondary) hover:text-(--color-text-primary) transition-colors"
          aria-label={t('common.close')}
        >
          <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
        </button>
      </div>

      {formError && (
        <div className="alert alert-error mb-4 flex items-center gap-2">
          <span>{formError}</span>
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-6">
        <EventFormFields
          formData={formData}
          venues={venues}
          onFormDataChange={onFormDataChange}
          disabled={isSaving}
          eventId={eventId}
          onDateQuotaError={onDateQuotaError}
          originalDate={originalDate}
          planType={planType}
          oneShotExpiresAt={oneShotExpiresAt}
        />

        {/* <div>
          <label className="input-label mb-3 block">
            {t('events.status')}
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {[
              { value: 'draft', label: t('events.statusDraft'), icon: faClock },
              { value: 'active', label: t('events.statusActive'), icon: faCircle },
              { value: 'completed', label: t('events.statusCompleted'), icon: faCheckCircle },
              { value: 'cancelled', label: t('events.statusCancelled'), icon: faTimesCircle },
            ].map((status) => (
              <button
                key={status.value}
                type="button"
                onClick={() => onFormDataChange({ status: status.value as Event['status'] })}
                className={`btn btn-sm flex flex-col items-center justify-center gap-1 py-2 ${
                  formData.status === status.value ? 'btn-primary' : 'btn-secondary'
                }`}
                disabled={isSaving}
              >
                <FontAwesomeIcon icon={status.icon} className="w-4 h-4" />
                <span className="text-xs">{status.label}</span>
              </button>
            ))}
          </div>
        </div> */}

        <div className="flex items-center justify-end gap-3 pt-4 border-t border-(--color-border)">
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-secondary"
            disabled={isSaving}
          >
            {t('common.cancel')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={isSaving}>
            {isSaving ? (
              <>
                <Spinner size="sm" variant="white" />
                <span className="ml-2">{t('common.save')}...</span>
              </>
            ) : (
              t('common.save')
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

